<template>
  <div class="image-container">
    <div
      v-if="canAddMore"
      class="qp-upload-dropzone"
      @click="$refs.fileInput.click()"
      @dragover.prevent
      @dragenter.prevent
      @drop.prevent="onDrop"
    >
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="qp-upload-dropzone__input"
        :id="idUpload || undefined"
        :multiple="multiple"
        @change="onFileChange"
        @click.stop
      />
      <svg class="add-image-svg" width="28" height="28" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2Z"/>
      </svg>
      <span class="drag-text">{{ dropText || dragText }}</span>
      <span class="qp-upload-dropzone__browse">{{ browseText }}</span>
    </div>

    <div v-if="images.length" class="image-list-container">
      <div v-for="(img, index) in images" :key="img.id || img.path" class="image-list-item">
        <img :src="img.path" :alt="img.caption" class="qp-upload-thumb" />
        <button type="button" class="image-icon-delete" title="Eliminar" @click="removeImage(index)">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
            <path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Reemplazo de 'vue-upload-multiple-image' (abandonado en 1.1.6, pinned a
// vue@^2.5.11, trae adentro su propio axios + vue-popperjs +
// vue-image-lightbox-carousel, todos Vue2 y también sin mantenimiento).
//
// Cubre solo lo que los 5 usos reales de la app necesitan: drag/browse
// múltiple, miniaturas, borrar con confirmación del padre, un evento por
// archivo subido con FormData, y precarga de imágenes existentes. No
// implementa el modal de edición ni el marcado de "imagen principal"
// porque ningún uso actual los activa (showEdit=false en los 5 lugares).
//
// Contrato (deducido leyendo los handlers reales en Upload-image.vue y
// Ecommerce.vue, no de la doc de la librería original):
// - fileList que viajan en los eventos son SIEMPRE la referencia interna
//   `images` (no una copia): Ecommerce.vue depende de que mutarla dentro
//   de `done()` sea visible en el `fileList` que ya tiene en mano.
// - cada item nuevo lleva `name` (del File original) porque
//   Upload-image.vue hace `fileList[index].name` antes de subirlo a S3.
export default {
  name: 'VueUploadMultipleImage',
  props: {
    dragText: { type: String, default: 'Arrastra tus imágenes aquí' },
    browseText: { type: String, default: 'Buscar imagen' },
    dropText: { type: String, default: '' },
    primaryText: { type: String, default: '' },
    markIsPrimaryText: { type: String, default: '' },
    popupText: { type: String, default: '' },
    showEdit: { type: Boolean, default: true },
    multiple: { type: Boolean, default: true },
    maxImage: { type: Number, default: 5 },
    dataImages: { type: Array, default: () => [] },
    idUpload: { type: String, default: '' },
    idEdit: { type: String, default: '' },
  },
  data() {
    return {
      images: [],
    };
  },
  computed: {
    canAddMore() {
      return this.images.length < this.maxImage;
    },
  },
  watch: {
    // deep porque los dos consumidores reales actualizan esto de formas
    // distintas: uno reasigna el array (Upload-image.vue), el otro le
    // hace .push() a la misma referencia ya pasada (Ecommerce.vue). Esto
    // solo pasa una vez, cuando el padre termina de cargar las imágenes
    // existentes tras su propio fetch async — después de eso el padre no
    // vuelve a tocar esta prop, así que no pisa las subidas del usuario.
    dataImages: {
      deep: true,
      immediate: true,
      handler(val) {
        this.images = (val || []).map((img) => ({ ...img }));
      },
    },
  },
  methods: {
    onDrop(e) {
      this.addFiles(e.dataTransfer.files);
    },
    onFileChange(e) {
      this.addFiles(e.target.files);
      e.target.value = '';
    },
    addFiles(fileListArg) {
      const files = Array.from(fileListArg || []);
      const remaining = this.maxImage - this.images.length;
      files.slice(0, remaining).forEach((file) => this.addFile(file));
    },
    addFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const item = {
          name: file.name,
          path: e.target.result,
          default: 1,
          highlight: 0,
          caption: '',
        };
        this.images.push(item);
        const index = this.images.indexOf(item);
        const formData = new FormData();
        formData.append('file', file);
        this.$emit('upload-success', formData, index, this.images);
      };
      reader.readAsDataURL(file);
    },
    removeImage(index) {
      const formData = new FormData();
      this.$emit(
        'before-remove',
        index,
        () => {
          this.images.splice(index, 1);
        },
        this.images,
        formData
      );
    },
  },
};
</script>

<style>
/* Global a propósito (no scoped): antes vivía en Upload-image.vue y
   Ecommerce.vue lo usaba igual sin tener su propia copia — dependía de
   que el bundle incluyera ese bloque global. Centralizado acá, única
   fuente de verdad para el tag <vue-upload-multiple-image>. */
.image-container {
  background-color: var(--surface);
  border: 1px dashed var(--border);
  border-radius: 6px;
  padding: 12px;
}

.qp-upload-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 24px 12px;
  cursor: pointer;
  position: relative;
}

.qp-upload-dropzone__input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.drag-text {
  color: var(--text-muted);
}

.qp-upload-dropzone__browse {
  color: var(--primary, #2fa3e7);
  text-decoration: underline;
}

.add-image-svg {
  fill: var(--text-muted);
}

.image-list-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.image-list-container .image-list-item {
  position: relative;
  width: 90px;
  height: 90px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  overflow: hidden;
}

.qp-upload-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-icon-delete {
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 3px;
  padding: 2px;
  line-height: 0;
  cursor: pointer;
  fill: var(--text-muted);
}

.image-icon-delete:hover {
  fill: var(--text);
}
</style>
